/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * P-5 (S629) — reading and rewriting the language segment of a SHOP path.
 *
 * Note the difference from `desksharePath` in lib/auth-redirect.ts, and do not
 * merge the two: the Deskshare WEBSITE has no `/en/` prefix for English, while
 * every shop route carries one because `createLocalizeRouter` prefixes them all
 * with `/:lang`. The two rules genuinely differ, so they stay two functions.
 */

import {
  DEFAULT_LANGUAGE,
  isSupportedLang,
  toSupportedLang,
  type SupportedLanguage,
} from "@/lib/supported-languages";

/** The language named by a shop pathname, or null when it names none we speak. */
export function langFromPath(pathname: string): SupportedLanguage | null {
  const first = (pathname || "").split("/").filter(Boolean)[0] ?? "";
  return isSupportedLang(first) ? (first.toLowerCase() as SupportedLanguage) : null;
}

/**
 * True when the first path segment looks like a language but is not one we
 * speak — `/zz/cart`. N-12: this is the case that used to render in English
 * while every link on the page kept the `/zz/` prefix.
 */
export function hasUnsupportedLangPrefix(pathname: string): boolean {
  const first = (pathname || "").split("/").filter(Boolean)[0] ?? "";
  return /^[a-z]{2}(-[a-z]{2})?$/i.test(first) && !isSupportedLang(first);
}

/**
 * The same shop path with its language segment replaced.
 *
 * Used by the switcher. The rest of the address — the remaining path segments,
 * the query string and the fragment — is carried over untouched, so switching
 * language on a cart that carries a desktop payload does not throw the payload
 * away.
 */
export function withLang(
  pathname: string,
  lang: unknown,
  search = "",
  hash = "",
): string {
  const target = toSupportedLang(lang);
  const segments = (pathname || "").split("/").filter(Boolean);
  const first = segments[0] ?? "";
  const looksLikeLang = /^[a-z]{2}(-[a-z]{2})?$/i.test(first);
  const rest = looksLikeLang ? segments.slice(1) : segments;
  const path = `/${[target, ...rest].join("/")}`;
  return `${path}${search || ""}${hash || ""}`;
}

/** Build `/<lang>/<page>` for a shop page, defaulting to English. */
export function localizedPath(lang: unknown, page = ""): string {
  const target = isSupportedLang(lang) ? lang.toLowerCase() : DEFAULT_LANGUAGE;
  const trimmed = (page || "").replace(/^\/+/, "");
  return trimmed ? `/${target}/${trimmed}` : `/${target}`;
}
